// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general components..
import Skeleton from './components/Skeleton.vue';

// Import general functions for shared functionality.
import { setCookie, getCookie, deleteCookie } from '../functions.js';
import constants from '../constants.js';

// Import axios for API consuming in a clean interface.
import axios from 'axios';

// Import Swiper functionality and set defaults.
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

Swiper.use([Pagination, Navigation]);

import { swiperConfig } from '../vendor/swiper.js';

const defaultApp = {
    mixins: [mixin],
    components: { Skeleton },
    data() {
        return {
            dataApi: '',
            dataNode: '',
            dataLimit: 100, // Default limit in BE
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataUncategorizedLabel: '',
            dataWidget: false,
            dataOverruleIds: [],
            culture: 'nl-NL',
            loading: false,
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            blockView: true,
            page: 1,
            searchText: '',

            constants: constants,
        };
    },
    // Watcher for pushing view preference to cookie for returning visitors.
    watch: {
        blockView(newView) {
            if (!newView) {
                setCookie(this.constants.cookies.listBlockView, 'false', 365);
            } else {
                deleteCookie(this.constants.cookies.listBlockView);
            }
        },
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets && this.data.facets.length;
        },
        itemsSet() {
            return this.data.items && this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet && this.data.facets.some((facet) => facet.values.length)) || this.dataOverruleIds.length;
        },
        filtersActive() {
            return (this.facetsSet && this.data.facets.some((facet) => facet.selected)) || this.searchText;
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                },
            ];
        },
        filterItems() {
            if (!this.filtersChosen) return [];
            if (this.dataOverruleIds.length) {
                return [
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in,
                    },
                ];
            }
            return this.data.facets
                .map((facet) => {
                    if (facet.values.length) {
                        return {
                            Key: facet.alias,
                            Values: facet.values,
                            Operator: this.constants.operators.in,
                        };
                    }
                })
                .filter((facet) => facet);
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.data && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText,
            };
        },
        amountsSentence() {
            if (!this.data || !this.data.queryCount || !this.data.totalCount) return null;
            return this.dataAmountsSentence.replace(this.constants.magicStrings.queryCount, this.data.queryCount).replace(this.constants.magicStrings.totalCount, this.data.totalCount);
        },
    },
    methods: {
        async init() {
            // Set loading to true.
            this.loading = true;

            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget;
            if (this.$el.dataset.overruleids) this.dataOverruleIds = this.$el.dataset.overruleids.split(',');
            if (this.$el.dataset.currentculture) this.culture = this.$el.dataset.currentculture;
            if (this.$el.dataset.node) this.dataNode = this.$el.dataset.node;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api;
            this.dataApi && !this.dataNode ? (this.dataApi += '?culture=' + this.culture) : (this.dataApi += '/' + this.dataNode);
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortkey) this.dataSortKey = this.$el.dataset.sortkey;
            if (this.$el.dataset.sortdirection) this.dataSortDirection = this.$el.dataset.sortdirection;
            if (this.$el.dataset.showxamountoftotalitems) this.dataAmountsSentence = this.$el.dataset.showxamountoftotalitems;
            if (this.$el.dataset.uncategorizedLabel) this.dataUncategorizedLabel = this.$el.dataset.uncategorizedLabel;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            // Preset values for facets based on query params.
            if (filterParams) {
                const facetsResponse = await this.fetchData();
                this.setFacets(facetsResponse);
                this.setFacetValuesFromUrl(filterParams);
            }

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            let tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData();
            let replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }

            // Set loading to false.
            this.loading = false;
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items.
        async fetchData() {
            try {
                const url = this.dataApi;
                const response = await axios.post(url, this.queryModel);
                return response;
            } catch (error) {
                console.log(error);
            }
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData() {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore() {
            this.page++;
            const response = await this.fetchData();
            this.setData(response);
            this.updateUrl();
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            } else {
                response.data.items.forEach((item) => {
                    this.data.items.push(item);
                });
            }
            this.setFacets(response);
        },
        // Method to set facet values based on URL search params.
        setFacetValuesFromUrl(filterParams) {
            if (!this.facetsSet) return;
            this.data.facets.forEach((facet) => {
                if (filterParams.has(facet.alias)) {
                    let valueArray = filterParams.get(facet.alias).split(',');
                    facet.values = valueArray.map((alias) => facet.options.find((option) => option.valueAlias === alias)).map((option) => option.valueAlias);
                }
            });
        },
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((facet) => ({
                    ...facet,
                    values: facet.options.filter((option) => option.selected).map((option) => option.valueAlias),
                }));
            this.data.queryCount = response.data.queryCount;
            this.data.totalCount = response.data.totalCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        // Data alteration function for reloading data with set filters.
        toggleOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find((facet) => facet.alias === facetAlias);
            if (changedFacet) {
                // If the option clicked is already selected, deselect it
                if (changedFacet.values.includes(optionAlias)) {
                    changedFacet.values = []; // Clear the selected values for this facet
                } else {
                    // Otherwise, select the new option and deselect any others
                    changedFacet.values = [optionAlias]; // Only this option will be selected
                }

                // Fetch initial data after changing the selection.
                this.fetchInitialData();
            }
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach((facet) => (facet.values = []));
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach((filterItem) => urlParams.append(filterItem.Key, filterItem.Values.join()));
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index, date) {
            let returnVal = null;
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
            } else {
                let prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.date).getMonth() || this.toDateObj(date).getYear() !== this.toDateObj(prevEvent.date).getYear()) {
                        returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                }
            }
            return returnVal;
        },
        initSlider() {
            if (this.dataWidget && this.$refs.slider) {
                const $slider = this.$refs.slider;
                new Swiper($slider, {
                    ...swiperConfig,
                });
            }
        },
    },
    created() {
        // Check which view is set as cookie.
        if (getCookie(this.constants.cookies.listBlockView) !== '') this.blockView = false;
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    },
    updated() {
        this.initSlider();
    },
};

export default defaultApp;
